"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Store = void 0;
const model_1 = require("../../data/model");
const multimap_1 = require("../../util/multimap");
const Identity_1 = require("../../data/identity/Identity");
const RSAKeyPair_1 = require("../../data/identity/RSAKeyPair");
const logging_1 = require("../../util/logging");
const OpCausalHistory_1 = require("../../data/history/OpCausalHistory");
class Store {
    constructor(backend) {
        this.backend = backend;
        this.backend.setStoredObjectCallback(async (literal) => {
            await this.fireCallbacks(literal);
        });
        this.classCallbacks = new multimap_1.MultiMap();
        this.referencesCallbacks = new multimap_1.MultiMap();
        this.classReferencesCallbacks = new multimap_1.MultiMap();
    }
    static registerBackend(name, loader) {
        Store.backendLoaders.set(name, loader);
    }
    static load(backendName, dbName) {
        const loader = Store.backendLoaders.get(backendName);
        if (loader === undefined) {
            return undefined;
        }
        else {
            return new Store(loader(dbName));
        }
    }
    // save & saveWithContext: the saving of operations is not recursive, you can't save all
    //                         operations in a mutable object that was referenced in one of
    //       (* note 1)        this object's dependencies. You need to call save explicitly
    //                         on the mutable object whose operations you want saved.
    setResources(resources) {
        this.resources = resources;
    }
    getResources() {
        return this.resources;
    }
    async save(object) {
        let context = object.toContext();
        let hash = context.rootHashes[0];
        for (const [hash, obj] of context.objects.entries()) {
            const author = obj.getAuthor();
            if (author !== undefined) {
                if (obj.shouldSignOnSave()) {
                    obj.setLastSignature(await author.sign(hash));
                    context.literals.get(hash).signature = obj.getLastSignature();
                }
                if (!obj.hasLastSignature()) {
                    throw new Error('Cannot save ' + hash + ', its signature is missing');
                }
            }
        }
        let missing = await this.findMissingReferencesWithContext(hash, context);
        if (missing.size > 0) {
            Store.operationLog.debug(() => 'Cannot save ' + hash + ' because the following references are missing: ' + Array.from(missing).join(', ') + '.');
            throw new Error('Cannot save object ' + hash + ' because the following references are missing: ' + Array.from(missing).join(', ') + '.');
        }
        let history = undefined;
        if (object instanceof model_1.MutationOp) {
            const prevOpCausalHistories = new Map();
            if (object.prevOps !== undefined) {
                for (const hashRef of object.prevOps.values()) {
                    const prevOpHistory = await this.loadOpCausalHistory(hashRef.hash);
                    if (prevOpHistory === undefined) {
                        throw new Error('Causal history of prevOp ' + hashRef.hash + ' of op ' + hash + ' is missing from store, cannot save');
                    }
                    prevOpCausalHistories.set(hashRef.hash, prevOpHistory);
                }
            }
            const opHistory = new OpCausalHistory_1.OpCausalHistory(object, prevOpCausalHistories);
            const opComputedProps = OpCausalHistory_1.OpCausalHistory.computeProps(prevOpCausalHistories);
            history = {
                literal: opHistory.literalize(),
                computed: opComputedProps
            };
        }
        Store.operationLog.debug(() => 'Saving object with hash ' + hash + ' .');
        await this.saveWithContext(hash, context, history);
        if (object instanceof model_1.MutableObject) {
            let queuedOps = await object.saveQueuedOps(); // see (* note 1) above
            if (queuedOps) {
                Store.operationLog.debug(() => 'Saved queued ops for object with hash ' + hash + ' .');
            }
        }
    }
    async findMissingReferencesWithContext(hash, context, expectedClassName) {
        let literal = context.literals.get(hash);
        if (literal === undefined) {
            return new Set([hash]);
        }
        if (expectedClassName !== undefined && literal.value['_class'] !== expectedClassName) {
            throw new Error('Referenced depency ' + hash + ' was found in the store with type ' + literal.value['_class'] + ' but was declared as being ' + expectedClassName + '.');
        }
        let missing = new Set();
        for (let dependency of literal.dependencies) {
            let depHash = dependency.hash;
            let dep = context.literals.get(depHash);
            if (dep === undefined) {
                let storedDep = await this.load(depHash);
                if (storedDep === undefined) {
                    missing.add(depHash);
                }
                else {
                    if (storedDep.getClassName() !== dependency.className) {
                        throw new Error('Referenced depency ' + dependency.hash + ' was found in the store with type ' + storedDep.getClassName() + ' but was declared as being ' + dependency.className + ' on path ' + dependency.path + '.');
                    }
                }
            }
            else {
                let depMissing = await this.findMissingReferencesWithContext(dependency.hash, context, dependency.className);
                for (const missingHash of depMissing) {
                    missing.add(missingHash);
                }
            }
        }
        return missing;
    }
    async saveWithContext(hash, context, history) {
        // TODO: we could keep a set of hashes already saved by previous calls to
        //       saveWithContext in this same recursion, and skip them without
        //       having to call this.load(hash).
        let loaded = await this.load(hash);
        if (loaded === undefined) {
            let object = context.objects.get(hash);
            object.setStore(this);
            object.setLastHash(hash);
            let literal = context.literals.get(hash);
            if (literal === undefined) {
                throw new Error('Hash ' + hash + ' is missing from context received for saving');
            }
            for (let dependency of literal.dependencies) {
                if (dependency.type === 'literal') {
                    await this.saveWithContext(dependency.hash, context);
                }
            }
            await this.backend.store(literal, history);
        }
        else {
            for (const [hash, obj] of context.objects.entries()) {
                obj.setStore(this);
                obj.setLastHash(hash);
            }
        }
    }
    async fireCallbacks(literal) {
        // fire watched classes callbacks
        for (const key of this.classCallbacks.keys()) {
            let className = Store.classForkey(key);
            if (literal.value['_class'] === className) {
                for (const callback of this.classCallbacks.get(key)) {
                    await callback(literal.hash);
                }
            }
        }
        // fire watched references callbacks
        for (const key of this.referencesCallbacks.keys()) {
            let reference = Store.referenceForKey(key);
            for (const dep of literal.dependencies) {
                if (dep.path === reference.path && dep.hash === reference.hash) {
                    for (const callback of this.referencesCallbacks.get(key)) {
                        await callback(literal.hash);
                    }
                }
            }
        }
        // fire watched class+reference pair callbacks
        for (const key of this.classReferencesCallbacks.keys()) {
            let classReference = Store.classReferenceForKey(key);
            if (classReference.className === literal.value['_class']) {
                for (const dep of literal.dependencies) {
                    if (dep.path === classReference.path && dep.hash === dep.hash) {
                        for (const callback of this.classReferencesCallbacks.get(key)) {
                            await callback(literal.hash);
                        }
                    }
                }
            }
        }
    }
    async loadLiteral(hash) {
        return this.backend.load(hash);
    }
    async loadRef(ref) {
        let obj = await this.load(ref.hash);
        if (obj !== undefined && ref.className !== obj.getClassName()) {
            throw new Error('Error loading reference to ' + ref.className + ': object with hash ' + ref.hash + ' has class ' + obj.getClassName() + ' instead.');
        }
        return obj;
    }
    async load(hash) {
        let context = new model_1.Context();
        context.resources = this.resources;
        return this.loadWithContext(hash, context);
    }
    async loadWithContext(hash, context) {
        var _a, _b;
        let obj = context.objects.get(hash);
        if (obj === undefined) {
            // load object's literal and its dependencies' literals into the context, if necessary
            let literal = context.literals.get(hash);
            if (literal === undefined) {
                literal = await this.loadLiteral(hash);
                if (literal === undefined) {
                    return undefined;
                }
                context.literals.set(literal.hash, literal);
            }
            for (let dependency of literal.dependencies) {
                if (dependency.type === 'literal') {
                    if (((_b = (_a = context.resources) === null || _a === void 0 ? void 0 : _a.aliasing) === null || _b === void 0 ? void 0 : _b.get(dependency.hash)) === undefined &&
                        context.objects.get(dependency.hash) === undefined &&
                        context.literals.get(dependency.hash) === undefined) {
                        // NO NEED to this.loadLiteralWithContext(depLiteral as Literal, context)
                        // because all transitive deps are in object deps.
                        let depLiteral = await this.loadLiteral(dependency.hash);
                        context.literals.set(dependency.hash, depLiteral);
                    }
                }
            }
            // use the context to create the object from all the loaded literals
            obj = model_1.HashedObject.fromContext(context, literal.hash);
            for (const ctxObj of context.objects.values()) {
                if (!ctxObj.hasStore()) {
                    ctxObj.setStore(this);
                }
            }
            if (obj instanceof Identity_1.Identity) {
                const id = obj;
                if (!id.hasKeyPair()) {
                    let kp = await this.load(id.getKeyPairHash());
                    if (kp !== undefined && kp instanceof RSAKeyPair_1.RSAKeyPair) {
                        id.addKeyPair(kp);
                    }
                }
            }
        }
        return obj;
    }
    async loadByClass(className, params) {
        let searchResults = await this.backend.searchByClass(className, params);
        return this.loadSearchResults(searchResults);
    }
    async loadByReference(referringPath, referencedHash, params) {
        let searchResults = await this.backend.searchByReference(referringPath, referencedHash, params);
        return this.loadSearchResults(searchResults);
    }
    async loadByReferencingClass(referringClassName, referringPath, referencedHash, params) {
        let searchResults = await this.backend.searchByReferencingClass(referringClassName, referringPath, referencedHash, params);
        return this.loadSearchResults(searchResults);
    }
    async loadOpCausalHistory(opHash) {
        const stored = await this.backend.loadOpCausalHistory(opHash);
        if (stored === undefined) {
            return undefined;
        }
        else {
            const opCausalHistory = new OpCausalHistory_1.OpCausalHistory(stored.literal);
            opCausalHistory._computedProps = stored.computed;
            return opCausalHistory;
        }
    }
    async loadOpCausalHistoryByHash(causalHistoryHash) {
        const stored = await this.backend.loadOpCausalHistoryByHash(causalHistoryHash);
        if (stored === undefined) {
            return undefined;
        }
        else {
            const opCausalHistory = new OpCausalHistory_1.OpCausalHistory(stored.literal);
            opCausalHistory._computedProps = stored.computed;
            return opCausalHistory;
        }
    }
    /*private async loadLiteral(hash: Hash) : Promise<Literal | undefined> {

        let packed = await this.backend.load(hash);
        
        if (packed === undefined) {
            return undefined;
        } else {

            return this.unpackLiteral(packed);
        }
       
    }*/
    /*private unpackLiteral(packed: PackedLiteral) : Literal {
        let literal = {} as Literal;

        literal.hash = packed.hash;
        literal.value = packed.value;
        literal.dependencies = new Set<Dependency>(packed.dependencies);
        if (packed.author !== undefined) {
            literal.author = packed.author;
        }
        literal.value['_flags'] = packed.flags;

        return literal;
    }*/
    async loadSearchResults(searchResults) {
        let context = new model_1.Context();
        let objects = [];
        for (let literal of searchResults.items) {
            context.literals.set(literal.hash, literal);
            let obj = await this.loadWithContext(literal.hash, context);
            objects.push(obj);
        }
        return { objects: objects, start: searchResults.start, end: searchResults.end };
    }
    async loadTerminalOpsForMutable(hash) {
        let info = await this.backend.loadTerminalOpsForMutable(hash);
        return info;
    }
    async loadClosure(init, next) {
        let closure = new Set();
        let pending = new Set(init);
        while (pending.size > 0) {
            let { done, value } = pending.values().next();
            done;
            pending.delete(value);
            closure.add(value);
            let obj = await this.load(value);
            let children = next(obj);
            for (const hash of children.values()) {
                if (!closure.has(hash)) {
                    pending.add(hash);
                }
            }
        }
        return closure;
    }
    watchClass(className, callback) {
        const key = Store.keyForClass(className);
        this.classCallbacks.add(key, callback);
    }
    watchReferences(referringPath, referencedHash, callback) {
        const key = Store.keyForReference(referringPath, referencedHash);
        this.referencesCallbacks.add(key, callback);
    }
    watchClassReferences(referringClassName, referringPath, referencedHash, callback) {
        const key = Store.keyForClassReference(referringClassName, referringPath, referencedHash);
        this.classReferencesCallbacks.add(key, callback);
    }
    removeClassWatch(className, callback) {
        const key = Store.keyForClass(className);
        return this.classCallbacks.delete(key, callback);
    }
    removeReferencesWatch(referringPath, referencedHash, callback) {
        const key = Store.keyForReference(referringPath, referencedHash);
        return this.referencesCallbacks.delete(key, callback);
    }
    removeClassReferencesWatch(referringClassName, referringPath, referencedHash, callback) {
        const key = Store.keyForClassReference(referringClassName, referringPath, referencedHash);
        return this.classReferencesCallbacks.delete(key, callback);
    }
    getName() {
        return this.backend.getName();
    }
    getBackendName() {
        return this.backend.getBackendName();
    }
    static keyForClass(className) {
        return className;
    }
    static keyForReference(referringPath, referencedHash) {
        return referringPath + '#' + referencedHash;
    }
    static keyForClassReference(referringClassName, referringPath, referencedHash) {
        return referringClassName + '->' + referringPath + '#' + referencedHash;
    }
    static classForkey(key) {
        return key;
    }
    static referenceForKey(key) {
        let parts = key.split('#');
        return { path: parts[0], hash: parts[1] };
    }
    static classReferenceForKey(key) {
        let parts = key.split('->');
        let className = parts[0];
        let result = Store.referenceForKey(parts[1]);
        result['className'] = className;
        return result;
    }
}
exports.Store = Store;
Store.operationLog = new logging_1.Logger(model_1.MutableObject.name, logging_1.LogLevel.INFO);
Store.backendLoaders = new Map();
//# sourceMappingURL=Store.js.map